/**
 * ChatMessage Component
 * Renders individual message with theme support
 * Handles text, images, system messages, and bot messages
 * Theme-aware with Tailwind dark: prefix classes
 */

import { ChatMessageProps } from '../../types/liveChat';
import { cn } from '../../utils/cn';
import { format } from 'date-fns';

export const ChatMessage: React.FC<Omit<ChatMessageProps, 'theme'>> = ({
  message,
  isCurrentUser = false
}) => {
  const isBotMessage = message.senderType === 'bot';
  const isSystemMessage = message.type === 'system';
  const isUserMessage = message.senderType === 'user';
  const isAdminMessage = message.senderType === 'admin';

  // Determine message alignment and styling
  const isOwnMessage = isCurrentUser || isUserMessage;
  const bgColor = isOwnMessage
    ? 'bg-primary-600 text-white'
    : isBotMessage
    ? 'bg-gray-100 dark:bg-gray-700 text-gray-900 dark:text-gray-100'
    : 'bg-gray-100 dark:bg-gray-700 text-gray-900 dark:text-gray-100';

  const alignment = isOwnMessage ? 'justify-end' : 'justify-start';

  /**
   * Format timestamp
   */
  const timeString = format(new Date(message.createdAt), 'HH:mm');

  /**
   * Handle system message
   */
  if (isSystemMessage) {
    return (
      <div className="flex justify-center py-2">
        <p className="text-xs px-2 py-1 rounded text-gray-500 dark:text-gray-400 bg-gray-200/50 dark:bg-gray-800/50">
          {message.content}
        </p>
      </div>
    );
  }

  /**
   * Handle bot acknowledgement
   */
  if (message.type === 'bot_acknowledgement') {
    return (
      <div className="flex justify-center py-2">
        <div className="text-xs px-3 py-2 rounded-lg max-w-xs bg-blue-100/50 dark:bg-blue-900/30 text-blue-900 dark:text-blue-200 border border-blue-200 dark:border-blue-800">
          <div className="flex items-center gap-2">
            <div className="w-1.5 h-1.5 rounded-full bg-blue-400 animate-pulse" />
            <p>{message.content}</p>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className={cn('flex gap-2', alignment)}>
      {/* Admin/Bot message - show avatar placeholder */}
      {!isOwnMessage && !isBotMessage && (
        <div className="w-7 h-7 rounded-full flex items-center justify-center flex-shrink-0 text-xs font-semibold bg-primary-600 dark:bg-primary-700 text-white">
          {message.senderName?.charAt(0) || 'A'}
        </div>
      )}

      {/* Message bubble */}
      <div className="flex flex-col gap-1 max-w-xs sm:max-w-sm">
        {/* Sender name for admin/bot */}
        {!isOwnMessage && (message.senderName || isAdminMessage) && (
          <p className="text-xs font-medium text-gray-600 dark:text-gray-400">
            {message.senderName || 'Support'}
          </p>
        )}

        {/* Message content */}
        <div className={cn(
          'px-3 py-2 rounded-lg break-words',
          bgColor,
          isOwnMessage ? 'rounded-br-none' : 'rounded-bl-none'
        )}>
          {/* Text content */}
          <p className="text-sm leading-relaxed whitespace-pre-wrap">
            {message.content}
          </p>

          {/* Image attachment */}
          {message.imageAttachment && (
            <div className="mt-2">
              <img
                src={message.imageAttachment.base64Data}
                alt="Chat attachment"
                className="max-w-full h-auto rounded max-h-48"
              />
            </div>
          )}
        </div>

        {/* Timestamp and read status */}
        <div className={cn(
          'text-xs px-1 flex items-center gap-1',
          isOwnMessage
            ? 'text-gray-500 dark:text-gray-400 justify-end'
            : 'text-gray-500 dark:text-gray-400'
        )}>
          <span>{timeString}</span>
          {isOwnMessage && message.status === 'read' && (
            <span className="text-primary-600 dark:text-primary-400">✓✓</span>
          )}
          {isOwnMessage && message.status === 'delivered' && (
            <span className="text-gray-400 dark:text-gray-600">✓</span>
          )}
        </div>
      </div>
    </div>
  );
};